import { Accordion, AccordionDetails, Button, Card, CardContent, Dialog, DialogContent, Grid, Stack } from '@mui/material'
import { compact, difference, includes, isNaN, isNil, orderBy, uniq } from 'lodash'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import { CardTitle } from '../../../../../components/card/NewCard'
import { NoDataCard } from '../../../../../components/card/NoDataCard'
import { ALIGN, CardTable } from '../../../../../components/datatable/NewTable'
import { SimpleFilterField } from '../../../../../components/datatable/SimpleSelectionTable'
import SimpleDatePicker from '../../../../../components/forms/SimpleDatePicker'
import SuperMultiAutocomplete from '../../../../../components/forms/SuperMultiAutocomplete'
import SelectionTableModal from '../../../../../components/modal/SelectionTableModal'
import StepperDialog from '../../../../../components/modal/StepperDialog'
import { AccordionTitle } from '../../../../../components/styled/Accordions'
import { DefaultDialogTitle } from '../../../../../components/styled/Dialog'
import { nbPerPageLabel, nbPerPageLabelShort, nbPerPageLabelTiny } from '../../../../../referencial/constants/ReferencialConstants'
import { whiteColor } from '../../../../../utils/constants/ColorTheme'
import useBoolean from '../../../../../utils/customHook/useBoolean'
import useCampaignObjectives from '../../../../../utils/customHook/useCampaignObjectives'
import useListIndexed from '../../../../../utils/customHook/useListIndexed'
import { hybridSearch } from '../../../../../utils/SearchUtil'
import { normalizeString } from '../../../../../utils/StringUtil'
import { stationCampaignPlanningExceptionShape, stationCampaignPlanningShape } from '../../../../shapes/CampaignShapes'
import { getPlanningProducers, getStationPlanningObjectiveIds } from '../../../../utils/CampaignUtils'
import { StationCampaignContext } from '../../DashboardCampaign'
import { FileDownload } from '@mui/icons-material'

const DEFAULT_EXCEPTION = {
    id: undefined,
    planningId: undefined,
    stationsPlan: [],
    objectivesToAddPlan: [],
    objectivesToDeletePlan: [],
}

const STEP = {
    STATION: 0,
    OBJECTIVE: 1,
    EXCEPTION: 2,
}

export const useFormattedStations = () => {
    const {
        campaignStations,
        stations,
    } = useContext(StationCampaignContext)
    const {
        citiesIndex,
    } = useSelector(store => ({
        citiesIndex: store.CityReducer.citiesIndex,
    }), shallowEqual)

    const indexedStations = useListIndexed(stations, 'id')

    return useMemo(() => orderBy(campaignStations, 'visitOrder').map(campaignStation => {
        const station = indexedStations[campaignStation.stationId] ?? {}
        const city = citiesIndex[station.townCode]
        return {
            id: campaignStation.id,
            code: station.code,
            name: { value: station.name, tooltip: station.name },
            city: { value: city?.name ?? '', tooltip: city?.name ?? '' },
            _normalizedSearchString: [station.code, station.name, city?.name].map(s => normalizeString(s)),
        }
    }), [campaignStations, indexedStations, citiesIndex])
}

const useProducerOptions = () => {
    const {
        campaign,
    } = useContext(StationCampaignContext)
    const {
        contacts,
    } = useSelector(store => ({
        contacts: store.ContactReducer.contacts,
    }), shallowEqual)

    return useMemo(() => {
        const campaignContactIds = uniq(compact([campaign.contactCode, ...(campaign.secondaryContacts || [])])).map(code => parseInt(code))
        return contacts.filter(({ id }) => includes(campaignContactIds, id))
    }, [contacts, campaign.contactCode, campaign.secondaryContacts])
}

export const useFormattedObjectives = () => {
    const {
        campaign,
        campaignObjectives,
    } = useContext(StationCampaignContext)
    const objectiveList = useCampaignObjectives(campaign.campaignType)

    return useMemo(() => orderBy(campaignObjectives, 'objectiveCode').map(objective => {
        const name = objectiveList.find(({ code }) => code === objective.objectiveCode)?.name
        return {
            id: objective.id,
            code: objective.objectiveCode,
            name,
            _normalizedSearchString: [name, `${objective.objectiveCode}`].map(s => normalizeString(s)),
        }
    }), [campaignObjectives, objectiveList])
}

const StepSelectStations = ({
    planning = {},
    setPlanning = () => {},
}) => {
    const [searchValue, setSearchValue] = useState()
    const formattedStations = useFormattedStations()
    const producerOptions = useProducerOptions()

    const rows = useMemo(() => hybridSearch(
        formattedStations.map(station => ({ ...station, isSelected: planning.stationsPlan.includes(station.id) })),
        normalizeString(searchValue),
    ), [formattedStations, planning.stationsPlan, searchValue])

    const toggleStation = ({ isSelected, id }) => setPlanning(prev => ({
        ...prev,
        stationsPlan: isSelected ? prev.stationsPlan.filter(stationId => stationId !== id) : [...prev.stationsPlan, id],
    }))

    const actions = rows.length > 0 && rows.every(({ isSelected }) => isSelected) ? [{
        onClick: () => setPlanning(prev => ({ ...prev, stationsPlan: difference(prev.stationsPlan, rows.map(({ id }) => id)) })),
        icon: 'check_box',
        tooltip: i18n.unselectAll,
    }] : [{
        onClick: () => setPlanning(prev => ({ ...prev, stationsPlan: uniq([...prev.stationsPlan, ...rows.map(({ id }) => id)]) })),
        icon: 'check_box_outline_blank',
        tooltip: i18n.selectAll,
    }]

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={2}>
                        <Grid size={2}>
                            <SimpleDatePicker
                                id='startDate'
                                label={i18n.startDate}
                                value={planning.startDate}
                                onChange={startDate => setPlanning(prev => ({ ...prev, startDate: isNil(startDate) || isNaN(startDate) ? undefined : startDate }))}
                                obligatory
                            />
                        </Grid>
                        <Grid size={2}>
                            <SimpleDatePicker
                                id='endDate'
                                label={i18n.endDate}
                                value={planning.endDate}
                                onChange={endDate => setPlanning(prev => ({ ...prev, endDate: isNil(endDate) || isNaN(endDate) ? undefined : endDate }))}
                                min={planning.startDate}
                            />
                        </Grid>
                        <Grid size='grow'>
                            <SuperMultiAutocomplete
                                label={i18n.completion}
                                options={producerOptions}
                                values={getPlanningProducers(planning)}
                                onChange={(producers = []) => setPlanning(prev => ({ ...prev, producers, producer: producers[0] }))}
                                multiple
                                limit={3}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            <Card>
                <CardContent>
                    <SimpleFilterField filter={{ searchValue }} setFilter={({ searchValue: value }) => setSearchValue(value)} />
                </CardContent>
            </Card>
            <CardTable
                title={i18n.stations}
                actions={actions}
                headers={['code', 'city', 'name']}
                rows={rows}
                onClickRow={toggleStation}
                lineActions={[{
                    icon: ({ isSelected }) => isSelected ? 'check_box' : 'check_box_outline_blank',
                    align: ALIGN.RIGHT,
                    onClick: toggleStation,
                }]}
                rowsPerPageOptions={nbPerPageLabel}
                maxHeight='45vh'
            />
        </Stack>
    )
}

StepSelectStations.propTypes = {
    planning: PropTypes.shape({}),
    setPlanning: PropTypes.func,
}

const StepSelectObjectives = ({
    planning = {},
    setPlanning = () => {},
}) => {
    const [searchValue, setSearchValue] = useState()
    const formattedObjectives = useFormattedObjectives()

    const rows = useMemo(() => hybridSearch(
        formattedObjectives.map(objective => ({ ...objective, isSelected: planning.objectivesPlan.includes(objective.id) })),
        normalizeString(searchValue),
    ), [formattedObjectives, planning.objectivesPlan, searchValue])

    const toggleObjective = ({ isSelected, id }) => setPlanning(prev => ({
        ...prev,
        objectivesPlan: isSelected ? prev.objectivesPlan.filter(objectiveId => objectiveId !== id) : [...prev.objectivesPlan, id],
    }))

    const actions = rows.length > 0 && rows.every(({ isSelected }) => isSelected) ? [{
        onClick: () => setPlanning(prev => ({ ...prev, objectivesPlan: difference(prev.objectivesPlan, rows.map(({ id }) => id)) })),
        icon: 'check_box',
        tooltip: i18n.unselectAll,
    }] : [{
        onClick: () => setPlanning(prev => ({ ...prev, objectivesPlan: uniq([...prev.objectivesPlan, ...rows.map(({ id }) => id)]) })),
        icon: 'check_box_outline_blank',
        tooltip: i18n.selectAll,
    }]

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <SimpleFilterField filter={{ searchValue }} setFilter={({ searchValue: value }) => setSearchValue(value)} />
                </CardContent>
            </Card>
            <CardTable
                title={i18n.objectives}
                actions={actions}
                headers={['name']}
                rows={rows}
                onClickRow={toggleObjective}
                lineActions={[{
                    icon: ({ isSelected }) => isSelected ? 'check_box' : 'check_box_outline_blank',
                    align: ALIGN.RIGHT,
                    onClick: toggleObjective,
                }]}
                rowsPerPageOptions={nbPerPageLabel}
                maxHeight='45vh'
            />
        </Stack>
    )
}

StepSelectObjectives.propTypes = {
    planning: PropTypes.shape({}),
    setPlanning: PropTypes.func,
}

const AccordionException = ({
    exception,
    index,
    setPlanningExceptions,
    formattedStations,
    formattedObjectives,
    planningObjectives,
}) => {
    const stationsPopin = useBoolean(false)
    const objectivesPopin = useBoolean(false)

    const analyzedObjectiveIds = [
        ...planningObjectives.filter(id => !includes(exception.objectivesToDeletePlan, id)),
        ...exception.objectivesToAddPlan,
    ]

    const stationRows = formattedStations.filter(({ id }) => includes(exception.stationsPlan, id))
    const objectiveRows = formattedObjectives.filter(({ id }) => includes(analyzedObjectiveIds, id))

    const onValidateStations = list => {
        setPlanningExceptions(prev => prev.map((planningException, i) => i === index ? { ...planningException, stationsPlan: list } : planningException))
        stationsPopin.setFalse()
    }

    const onValidateObjectives = list => {
        setPlanningExceptions(prev => prev.map((planningException, i) => i === index ? {
            ...planningException,
            objectivesToAddPlan: list.filter(id => !includes(planningObjectives, id)),
            objectivesToDeletePlan: planningObjectives.filter(id => !includes(list, id)),
        } : planningException))
        objectivesPopin.setFalse()
    }

    return (
        <Accordion key={index} defaultExpanded>
            <AccordionTitle
                title={`${i18n.exception} n°${index + 1}`}
                actions={[{
                    icon: 'close',
                    onClick: () => setPlanningExceptions(prev => prev.toSpliced(index, 1)),
                    color: whiteColor,
                }]}
            />
            <AccordionDetails>
                <Grid container spacing={1}>
                    <Grid size={6}>
                        <CardTable
                            title={i18n.selectedStations}
                            rows={stationRows}
                            headers={['code', { key: 'city', width: '150px' }, { key: 'name', width: '150px' }]}
                            rowsPerPageOptions={nbPerPageLabelTiny}
                            actions={[{ icon: 'compare_arrows', tooltip: i18n.edit, onClick: stationsPopin.setTrue }]}
                        />
                    </Grid>
                    <Grid size={6}>
                        <CardTable
                            title={i18n.analyzedObjectives}
                            rows={objectiveRows}
                            headers={['name']}
                            rowsPerPageOptions={nbPerPageLabelTiny}
                            actions={[{ icon: 'compare_arrows', tooltip: i18n.edit, onClick: objectivesPopin.setTrue }]}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
            <SelectionTableModal
                isOpen={stationsPopin.value}
                onClose={stationsPopin.setFalse}
                onValidate={onValidateStations}
                title={i18n.stations}
                dialogHeight='auto'
                listData={formattedStations}
                defaultSelectionList={exception.stationsPlan}
                listHeaders={['code', { key: 'city', width: '150px' }, { key: 'name', width: '150px' }]}
                listTitle={i18n.nonSelectedStations}
                selectionListTitle={i18n.selectedStations}
                filterField={SimpleFilterField}
                filterFunction={(list, { searchValue }) => hybridSearch(list, normalizeString(searchValue))}
                nbPerPage={nbPerPageLabelShort}
            />
            <SelectionTableModal
                isOpen={objectivesPopin.value}
                onClose={objectivesPopin.setFalse}
                onValidate={onValidateObjectives}
                title={i18n.objectives}
                dialogHeight='auto'
                listData={formattedObjectives}
                defaultSelectionList={analyzedObjectiveIds}
                listHeaders={['name']}
                listTitle={i18n.nonSelectedObjectives}
                selectionListTitle={i18n.selectedObjectives}
                filterField={SimpleFilterField}
                filterFunction={(list, { searchValue }) => hybridSearch(list, normalizeString(searchValue))}
                nbPerPage={nbPerPageLabelShort}
            />
        </Accordion>
    )
}

AccordionException.propTypes = {
    exception: PropTypes.shape({
        stationsPlan: PropTypes.arrayOf(PropTypes.number),
        objectivesToAddPlan: PropTypes.arrayOf(PropTypes.number),
        objectivesToDeletePlan: PropTypes.arrayOf(PropTypes.number),
    }),
    index: PropTypes.number,
    setPlanningExceptions: PropTypes.func,
    formattedStations: PropTypes.arrayOf(PropTypes.shape({})),
    formattedObjectives: PropTypes.arrayOf(PropTypes.shape({})),
    planningObjectives: PropTypes.arrayOf(PropTypes.number),
}

const StepSelectExceptions = ({
    planning = {},
    planningExceptions = [],
    setPlanningExceptions = () => {},
}) => {
    const formattedStations = useFormattedStations()
    const formattedObjectives = useFormattedObjectives()

    const planningStations = formattedStations.filter(({ id }) => includes(planning.stationsPlan, id))

    return (
        <Stack spacing={1}>
            <Card>
                <CardTitle
                    title={i18n.addException}
                    actions={[{
                        icon: 'add',
                        onClick: () => setPlanningExceptions(prev => [...prev, {
                            ...DEFAULT_EXCEPTION,
                            planningId: planning.id,
                            objectivesToDeletePlan: [...planning.objectivesPlan],
                        }]),
                    }]}
                />
            </Card>
            {planningExceptions.length === 0 && <NoDataCard />}
            {planningExceptions.map((exception, index) => (
                <AccordionException
                    key={index}
                    exception={exception}
                    index={index}
                    setPlanningExceptions={setPlanningExceptions}
                    formattedStations={planningStations}
                    formattedObjectives={formattedObjectives}
                    planningObjectives={planning.objectivesPlan}
                />
            ))}
        </Stack>
    )
}

StepSelectExceptions.propTypes = {
    planning: PropTypes.shape({}),
    planningExceptions: PropTypes.arrayOf(PropTypes.shape({})),
    setPlanningExceptions: PropTypes.func,
}

const PlanningReadMode = ({
    planning = {},
    planningExceptions = [],
    onExport,
}) => {
    const formattedStations = useFormattedStations()
    const formattedObjectives = useFormattedObjectives()
    const indexedObjectives = useListIndexed(formattedObjectives, 'id')
    const producerOptions = useProducerOptions()

    const planningStations = formattedStations.filter(({ id }) => includes(planning.stationsPlan, id))

    return (
        <Stack spacing={1}>
            <Card>
                <CardContent>
                    <Grid container spacing={2} alignItems='center' justifyContent={'space-between'}>
                        <Grid container size={'grow'}>
                            <SimpleDatePicker label={i18n.startDate} value={planning.startDate} readMode />
                            <SimpleDatePicker label={i18n.endDate} value={planning.endDate} readMode />
                            <SuperMultiAutocomplete
                                label={i18n.completion}
                                options={producerOptions}
                                values={getPlanningProducers(planning)}
                                multiple
                                readMode
                            />
                        </Grid>
                        <Grid container size={'auto'}>
                            <FileDownload onClick={onExport} sx={{ cursor: 'pointer' }} fontSize='large' />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
            {planningStations.map(station => {
                const effectiveIds = getStationPlanningObjectiveIds(planning, planningExceptions, station.id)
                return (
                    <CardTable
                        key={station.id}
                        title={`${station.code ?? ''} ${station.name?.value ?? ''}`}
                        rows={compact(effectiveIds.map(id => indexedObjectives[id]))}
                        headers={['name']}
                    />
                )
            })}
        </Stack>
    )
}

PlanningReadMode.propTypes = {
    planning: PropTypes.shape({}),
    planningExceptions: PropTypes.arrayOf(PropTypes.shape({})),
    onExport: PropTypes.func,
}

const PopinCampaignPlanning = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    campaignPlanning = {},
    campaignPlanningExceptions = [],
    isEditMode = true,
    onExport = () => {},
}) => {
    const { campaign, campaignStations, campaignObjectives } = useContext(StationCampaignContext)
    const [planning, setPlanning] = useState(campaignPlanning)
    const [planningExceptions, setPlanningExceptions] = useState(campaignPlanningExceptions)

    useEffect(() => {
        setPlanning(campaignPlanning)
        setPlanningExceptions(campaignPlanningExceptions)
        return () => setPlanning()
    }, [campaignPlanning, campaignPlanningExceptions, campaign])

    const updateExceptionsOnStationsChanges = () => setPlanningExceptions(prev => compact(prev.map(exception => {
        const cleaned = {
            ...exception,
            stationsPlan: exception.stationsPlan.filter(stationId => includes(planning.stationsPlan, stationId)),
        }
        return cleaned.stationsPlan.length ? cleaned : undefined
    })))

    const cleanExceptions = () => compact(planningExceptions.map(exception => {
        const cleaned = {
            ...exception,
            stationsPlan: exception.stationsPlan.filter(id => includes(campaignStations.map(({ id: stationId }) => stationId), id)),
            objectivesToAddPlan: exception.objectivesToAddPlan.filter(id => includes(campaignObjectives.map(({ id: objectiveId }) => objectiveId), id)),
            objectivesToDeletePlan: exception.objectivesToDeletePlan.filter(id => includes(planning.objectivesPlan, id)),
        }
        if (!cleaned.stationsPlan.length) return undefined
        if (!cleaned.objectivesToAddPlan.length && !cleaned.objectivesToDeletePlan.length) return undefined
        return cleaned
    }))

    if (!isEditMode) {
        return (
            <Dialog open={isOpen} onClose={onClose} fullWidth maxWidth='lg'>
                <DefaultDialogTitle title={i18n.planning} onClose={onClose} />
                <DialogContent>
                    <PlanningReadMode planning={planning} planningExceptions={planningExceptions} onExport={onExport} />
                </DialogContent>
            </Dialog>
        )
    }

    return (
        <StepperDialog
            steps={[
                {
                    label: i18n.stationsSelection,
                    constant: STEP.STATION,
                    nextAvailable: !isNil(planning.startDate) && (isNil(planning.endDate) || planning.startDate <= planning.endDate) && planning.stationsPlan.length > 0,
                    onNext: (nextStep) => {
                        if (isNil(planning.endDate)) {
                            setPlanning(prev => ({ ...prev, endDate: planning.startDate }))
                        }
                        updateExceptionsOnStationsChanges()
                        nextStep()
                    },
                },
                {
                    label: i18n.objectivesSelection,
                    constant: STEP.OBJECTIVE,
                },
                {
                    label: `${i18n.exceptions} (${i18n.option})`,
                    constant: STEP.EXCEPTION,
                },
            ]}
            open={isOpen}
            title={i18n.planning}
            closeDialog={onClose}
            renderSaveButton={step => step >= STEP.OBJECTIVE && (
                <Button onClick={() => onValidate(planning, cleanExceptions())} variant='contained'>
                    {i18n.validate}
                </Button>
            )}
            sx={{ minWidth: '90vw' }}
        >
            {step => (
                <>
                    {step === STEP.STATION && <StepSelectStations planning={planning} setPlanning={setPlanning} />}
                    {step === STEP.OBJECTIVE && <StepSelectObjectives planning={planning} setPlanning={setPlanning} />}
                    {step === STEP.EXCEPTION && (
                        <StepSelectExceptions
                            planning={planning}
                            planningExceptions={planningExceptions}
                            setPlanningExceptions={setPlanningExceptions}
                        />
                    )}
                </>
            )}
        </StepperDialog>
    )
}

PopinCampaignPlanning.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    campaignPlanning: stationCampaignPlanningShape,
    campaignPlanningExceptions: PropTypes.arrayOf(stationCampaignPlanningExceptionShape),
    isEditMode: PropTypes.bool,
    onExport: PropTypes.func,
}

export default PopinCampaignPlanning
